import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import { LoginPage } from "@/pages/LoginPage";
import { AuthCallback } from "@/pages/AuthCallback";
import { DashboardPage } from "@/pages/DashboardPage";
import { SitesPage } from "@/pages/SitesPage";
import { KnowledgeBasePage } from "@/pages/KnowledgeBasePage";
import { ArticleEditorPage } from "@/pages/ArticleEditorPage";
import { SuggestionsPage } from "@/pages/SuggestionsPage";
import { HandoffsPage } from "@/pages/HandoffsPage";
import { WidgetPreviewPage } from "@/pages/WidgetPreviewPage";
import { DocsPage } from "@/pages/DocsPage";
import { SchedulerPage } from "@/pages/SchedulerPage";
import { UsersPage } from "@/pages/UsersPage";
import { EmbedPage } from "@/pages/EmbedPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // If user data passed from AuthCallback, use it directly
  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-600">Indlæser...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children({ user, setUser });
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          {({ user, setUser }) => <DashboardPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/sites" element={
        <ProtectedRoute>
          {({ user, setUser }) => <SitesPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/knowledge-base" element={
        <ProtectedRoute>
          {({ user, setUser }) => <KnowledgeBasePage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/knowledge-base/new" element={
        <ProtectedRoute>
          {({ user, setUser }) => <ArticleEditorPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/knowledge-base/:articleId" element={
        <ProtectedRoute>
          {({ user, setUser }) => <ArticleEditorPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/suggestions" element={
        <ProtectedRoute>
          {({ user, setUser }) => <SuggestionsPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/handoffs" element={
        <ProtectedRoute>
          {({ user, setUser }) => <HandoffsPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/widget-preview" element={
        <ProtectedRoute>
          {({ user, setUser }) => <WidgetPreviewPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/docs" element={
        <ProtectedRoute>
          {({ user, setUser }) => <DocsPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/scheduler" element={
        <ProtectedRoute>
          {({ user, setUser }) => <SchedulerPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute>
          {({ user, setUser }) => <UsersPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="/embed" element={
        <ProtectedRoute>
          {({ user, setUser }) => <EmbedPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
